import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Radar, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { getMyCompetencies } from "services/api";
import { useAuth } from "context/authContext";
import LoadingScreen from "components/Loading";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/SpeedRounded";
// layouts\user\Competencies\myCompetencies\components\competencies\components\index.js
import MyCompetencyCards from "layouts/user/Competencies/myCompetencies/components/competencies/components";

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

export default function MyCompetencies() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const employeeId = location.state?.employeeId;
  const employeeName = location.state?.employeeName;
  const [competencies, setCompetencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const { employee, selectedMonth, selectedYear, setSelectedMonth, setSelectedYear } = useAuth();
  const [kpiLinks, setKpiLinks] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Define responsive values based on the breakpoint
  const chartPadding = isMobile ? 50 : 30;
  const tickFontSize = isMobile ? 8 : 10;

  const truncateLabel = (label, limit) => {
    if (!label) return "";
    return label.length > limit ? label.substring(0, limit) + "..." : label;
  };

  useEffect(() => {
    const idToLoad = employeeId !== undefined ? employeeId : employee.id;
    setLoading(true);
    getMyCompetencies(idToLoad, selectedMonth, selectedYear).then((raw) => {
      if (raw == "error") {
        setLoading(false);
        return;
      }
      const parsed = raw.map((row) => ({
        competencyName: row[0],
        targetLevelName: row[1],
        targetLevelValue: row[2],
        myLevelName: row[3],
        myLevelValue: row[4],
        trainingName: row[5],
        id: row[6],
        kpiLinks: row[7],
        compToEmpId: row[8],
      }));

      setCompetencies(parsed);
      setLoading(false);
    });
  }, [selectedMonth, selectedYear, location]);

  const labels = competencies.map((c) => c.competencyName);
  const targetData = competencies.map((c) => c.targetLevelValue);
  const myData = competencies.map((c) => c.myLevelValue);
  // const gapData = competencies.map((c) => (c.myLevelValue ?? 0) - c.targetLevelValue);

  // const gapData = competencies.map((c) => {
  //   // STRICT LOGIC: If either value is null or undefined, the gap is null
  //   if (c.myLevelValue == null || c.targetLevelValue == null) {
  //     return null;
  //   }
  //   // Otherwise, calculate the difference
  //   return c.myLevelValue - c.targetLevelValue;
  // });

  const gapData = competencies.map((c) => {
    let gap = null;

    if (
      c.targetLevelValue !== null &&
      c.targetLevelValue !== undefined &&
      c.myLevelValue !== null &&
      c.myLevelValue !== undefined
    ) {
      gap = c.myLevelValue - c.targetLevelValue;
    }

    return gap;
  });

  // const fullyMatched = competencies.filter(
  //   (c) => (c.myLevelValue ?? 0) >= c.targetLevelValue
  // ).length;
  // const gapCount = competencies.length - fullyMatched;

  // 2. Count "Fully Matched": Gap is NOT null AND gap is 0 or higher
  const fullyMatched = gapData.filter((g) => g !== null && g >= 0).length;

  // 3. Count "Gap": Gap is NOT null AND gap is negative (below 0)
  const gapCount = gapData.filter((g) => g !== null && g < 0).length;

  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false, // This allows us to control the height via CSS
    plugins: {
      legend: {
        position: "bottom",
        labels: { boxWidth: 12, usePointStyle: true, font: { size: 12 } },
      },
    },
  };

  const radarOptions = {
    ...commonOptions,
    layout: {
      padding: {
        top: 10,
        bottom: 10,
        left: chartPadding, // Responsive padding
        right: chartPadding, // Responsive padding
      },
    },
    scales: {
      r: {
        angleLines: { display: true, color: "#e2e8f0" },
        grid: { display: true, color: "#e2e8f0" },
        suggestedMin: 0,
        ticks: {
          display: true,
          stepSize: 1,
          backdropColor: "transparent",
          font: { size: tickFontSize }, // Responsive ticks
        },
        pointLabels: {
          display: true,
          font: {
            size: isMobile ? 9 : 11,
            weight: "600",
          },
          // TRUNCATE LOGIC:
          callback: (label) => {
            return isMobile ? truncateLabel(label, 12) : label;
          },
          color: "#64748b",
        },
      },
    },
  };

  const barOptions = {
    ...commonOptions,
    plugins: {
      ...commonOptions.plugins,
      legend: {
        ...commonOptions.plugins.legend,
        // THIS REMOVES THE INTERACTIVITY (The "On Click" logic)
        onClick: null,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: true, // 2. SHOW HORIZONTAL LINES
          color: "#f1f5f9", // Light slate color
          drawBorder: false,
        },
        ticks: { stepSize: 1 },
      },
      x: {
        grid: {
          display: true,
          color: "#f1f5f9",
        },
        ticks: {
          // --- TRUNCATE LOGIC FOR X-AXIS ---
          callback: function (value) {
            const label = this.getLabelForValue(value);
            if (isMobile) {
              return label.length > 12 ? label.substring(0, 12) + "..." : label;
            }
            return label;
          },
          font: {
            size: isMobile ? 10 : 12,
            weight: "600",
          },
        },
      },
    },
  };

  const radarData = {
    labels,
    datasets: [
      {
        label: t("user.competencies.target"),
        data: targetData,
        backgroundColor: "rgba(25, 118, 210, 0.2)",
        borderColor: "rgba(25, 118, 210, 1)",
        borderWidth: 2,
      },
      {
        label: t("user.competencies.myLevel"),
        data: myData,
        backgroundColor: "rgba(76, 175, 80, 0.2)",
        borderColor: "rgba(76, 175, 80, 1)",
        borderWidth: 2,
      },
    ],
  };

  const barData = {
    labels,
    datasets: [
      {
        label: t("user.competencies.differenceLabel"),
        data: gapData,
        minBarLength: 3,
        backgroundColor: gapData.map((g) => {
          // 1. If gap is null (missing data), return a transparent color
          if (g === null) return "rgba(0,0,0,0)";

          // 2. If gap is 0 or positive, return Green
          // 3. If gap is negative, return Red
          return g >= 0 ? "#108092" : "#ef5350";
        }),
      },
    ],
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" gutterBottom>
          {employeeName ? `${employeeName}'s Competencies` : t("user.competencies.myCompetencies")}
        </Typography>

        {/* Summary Cards */}
        <Grid container spacing={2} mb={2}>
          {/* Total Competencies Card */}
          <Grid item xs={12} sm={4}>
            <Card
              variant="outlined" // Uses the standard thin border
              sx={{
                borderRadius: 3, // Crisp, slightly rounded edges (sharp look)
                borderColor: "#e0e0e0", // Normal thin border color
                boxShadow: "none",
                backgroundColor: "#fff",
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  "&:last-child": { pb: 2 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "#f0f2ff",
                    p: 1.2,
                    borderRadius: 1, // Sharp matching corners for the icon box
                    display: "flex",
                    color: "#3f51b5",
                  }}
                >
                  <GridViewRoundedIcon fontSize="medium" />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: "#1a1a1a", lineHeight: 1.2 }}
                  >
                    {t("user.competencies.total")}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, my: 0.3 }}>
                    {competencies.length}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#64748b", // Professional Slate color
                      display: "block",
                      fontSize: "0.72rem", // Slightly smaller
                      fontWeight: 500, // Medium weight
                      lineHeight: 1.2, // Pulls it closer to the number
                      mt: 0.2, // Tiny margin-top
                    }}
                  >
                    {t("user.competencies.totalCompsDesc")}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Fully Matched Card */}
          <Grid item xs={12} sm={4}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 3,
                borderColor: "#e0e0e0",
                boxShadow: "none",
                backgroundColor: "#fff",
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  "&:last-child": { pb: 2 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "#effaf3",
                    p: 1.2,
                    borderRadius: 1,
                    display: "flex",
                    color: "#388e3c",
                  }}
                >
                  <VerifiedRoundedIcon fontSize="medium" />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: "#1a1a1a", lineHeight: 1.2 }}
                  >
                    {t("user.competencies.matched")}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, my: 0.3 }}>
                    {fullyMatched}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#64748b", // Professional Slate color
                      display: "block",
                      fontSize: "0.72rem", // Slightly smaller
                      fontWeight: 500, // Medium weight
                      lineHeight: 1.2, // Pulls it closer to the number
                      mt: 0.2, // Tiny margin-top
                    }}
                  >
                    {t("user.competencies.meetTarget")}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Gaps Card */}
          <Grid item xs={12} sm={4}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 3,
                borderColor: "#e0e0e0",
                boxShadow: "none",
                backgroundColor: "#fff",
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  "&:last-child": { pb: 2 },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "#fff1f1",
                    p: 1.2,
                    borderRadius: 1,
                    display: "flex",
                    color: "#d32f2f",
                  }}
                >
                  <WarningAmberRoundedIcon fontSize="medium" />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: "#1a1a1a", lineHeight: 1.2 }}
                  >
                    {t("user.competencies.gaps")}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, my: 0.3 }}>
                    {gapCount}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#64748b", // Professional Slate color
                      display: "block",
                      fontSize: "0.72rem", // Slightly smaller
                      fontWeight: 500, // Medium weight
                      lineHeight: 1.2, // Pulls it closer to the number
                      mt: 0.2, // Tiny margin-top
                    }}
                  >
                    {t("user.competencies.belowTarget")}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid container spacing={3} alignItems="stretch">
          {" "}
          {/* Radar Chart */}
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: "12px",
                background: "#ffffff",
                boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
                border: "1px solid #f1f5f9",
                width: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                {t("user.competencies.radarTitle")}
              </Typography>

              {/* Chart Wrapper with fixed height */}
              <Box
                sx={{
                  position: "relative",
                  height: { xs: 280, md: 350 }, // Shorter on mobile
                  width: "100%",
                  flexGrow: 1,
                }}
              >
                <Radar data={radarData} options={radarOptions} />
              </Box>
            </Paper>
          </Grid>
          {/* Bar Chart */}
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: "12px",
                background: "#ffffff",
                boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
                border: "1px solid #f1f5f9",
                width: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                {t("user.competencies.barTitle")}
              </Typography>

              {/* Chart Wrapper with same fixed height */}
              <Box sx={{ height: 350, width: "100%", flexGrow: 1 }}>
                <Bar data={barData} options={barOptions} />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Table */}
        <MyCompetencyCards competencies={competencies} t={t} />
      </Box>
    </>
  );
}
